import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Avatar,
  Stack,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Rating,
  alpha,
  useTheme
} from '@mui/material';
import {
  Security,
  LocationOn,
  BusinessCenter,
  MonetizationOn,
  TrendingUp,
  Assessment,
  Insights
} from '@mui/icons-material';

import ProgressChart from '../components/ProgressChart';
import { getProgressChartData } from '../data/mockData';
import { getTrendColor, getTrendChipColor, getTrendIcon } from '../utils/trendUtils';

const AnalyticsTab = ({ dashboardData }) => {
  const theme = useTheme();

  return (
    <Grid container spacing={3} sx={{ width: '100%' }}>
      {/* Financial Performance Analysis */}
      <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
        <ProgressChart 
          title="Financial Performance Metrics"
          subtitle="Investment portfolio analysis"
          icon={<Security />}
          data={getProgressChartData.financialMetrics(dashboardData)}
        />
      </Grid>

      {/* Location Performance */}
      <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
        <ProgressChart 
          title="Top Performing Counties"
          subtitle="Property count by location"
          icon={<LocationOn />}
          data={getProgressChartData.locationPerformance}
        />
      </Grid>

      {/* Financial Dashboard */}
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 3, background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)` }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Avatar sx={{ bgcolor: 'info.main' }}>
              <Insights />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Investment Portfolio Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Comprehensive financial performance overview
              </Typography>
            </Box>
          </Stack>
          
          <Grid container spacing={3} sx={{ width: '100%' }}>
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
              <Card 
                variant="outlined" 
                sx={{ 
                  textAlign: 'center', 
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <Box>
                  <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                    <MonetizationOn />
                  </Avatar>
                  <Typography variant="h3" color="success.main" fontWeight="bold" gutterBottom>
                    ${((dashboardData?.financialMetrics.totalInvestment || 2450000) / 1000000).toFixed(1)}M
                  </Typography>
                  <Typography variant="body1" color="text.secondary" fontWeight="medium">
                    Total Investment
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={85}
                  sx={{ mt: 2, height: 6, borderRadius: 3 }}
                  color="success"
                />
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
              <Card 
                variant="outlined" 
                sx={{ 
                  textAlign: 'center', 
                  p: 3,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <Box>
                  <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                    <TrendingUp />
                  </Avatar>
                  <Typography variant="h3" color="primary.main" fontWeight="bold" gutterBottom>
                    {dashboardData?.financialMetrics.expectedROI || 18.5}%
                  </Typography>
                  <Typography variant="body1" color="text.secondary" fontWeight="medium">
                    Expected ROI
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={75}
                  sx={{ mt: 2, height: 6, borderRadius: 3 }}
                  color="primary"
                />
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
              <Card 
                variant="outlined" 
                sx={{ 
                  textAlign: 'center', 
                  p: 3,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <Box>
                  <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                    <Security />
                  </Avatar>
                  <Typography variant="h3" color="warning.main" fontWeight="bold" gutterBottom>
                    {100 - (dashboardData?.financialMetrics.riskScore || 35)}%
                  </Typography>
                  <Typography variant="body1" color="text.secondary" fontWeight="medium">
                    Risk Management
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={65}
                  sx={{ mt: 2, height: 6, borderRadius: 3 }}
                  color="warning"
                />
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
              <Card 
                variant="outlined" 
                sx={{ 
                  textAlign: 'center', 
                  p: 3,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                <Box>
                  <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                    <Assessment />
                  </Avatar>
                  <Typography variant="h3" color="info.main" fontWeight="bold" gutterBottom>
                    {dashboardData?.financialMetrics.diversificationScore || 78}%
                  </Typography>
                  <Typography variant="body1" color="text.secondary" fontWeight="medium">
                    Diversification
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={78}
                  sx={{ mt: 2, height: 6, borderRadius: 3 }}
                  color="info"
                />
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Market Intelligence Table */}
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ overflow: 'hidden' }}>
          <Box sx={{ 
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: 'white',
            p: 3
          }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                <BusinessCenter />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Market Intelligence & County Performance
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Real-time market analysis across top performing counties
                </Typography>
              </Box>
            </Stack>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell><Typography fontWeight="bold">County</Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">Properties</Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">Avg Price</Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">Market Trend</Typography></TableCell>
                  <TableCell align="center"><Typography fontWeight="bold">Performance</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(dashboardData?.locations || []).map((location, index) => (
                  <TableRow 
                    key={location.county}
                    sx={{ 
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                      '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.grey[500], 0.02) }
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar 
                          sx={{ 
                            bgcolor: getTrendColor(location.trend, theme),
                            width: 32,
                            height: 32,
                            fontSize: '0.875rem'
                          }}
                        >
                          {location.county.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {location.county} County
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            New Jersey
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="h6" color="primary.main" fontWeight="bold">
                        {location.properties}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body1" fontWeight="medium">
                        ${location.avgPrice.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={location.trend} 
                        color={getTrendChipColor(location.trend)}
                        size="small"
                        icon={getTrendIcon(location.trend)}
                        sx={{ minWidth: 80 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={(location.properties / Math.max(...(dashboardData?.locations || []).map(l => l.properties))) * 100}
                          sx={{ 
                            width: 60, 
                            height: 6, 
                            borderRadius: 3,
                            bgcolor: alpha(theme.palette.grey[500], 0.2)
                          }}
                          color={getTrendChipColor(location.trend)}
                        />
                        <Rating 
                          value={location.trend === 'up' ? 5 : location.trend === 'down' ? 2 : 3}
                          max={5}
                          size="small"
                          readOnly
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default AnalyticsTab; 